export interface ClaudeResponse {
  message: string;
  usedTokens: number;
}

export interface ClaudeStreamEvent {
  text?: string;
  thinking?: string;
  type?: 'usage';
  usage?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
}

const STREAM_INACTIVITY_TIMEOUT_MS = 90000;

function readWithTimeout(
  reader: { read: () => Promise<{ value: any; done: boolean }> },
  timeoutMs: number,
): Promise<{ value: any; done: boolean }> {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () =>
        reject(
          new Error(
            'The model stopped responding (no data received). Please try running again.',
          ),
        ),
      timeoutMs,
    );
  });
  return Promise.race([reader.read(), timeout]).finally(() =>
    clearTimeout(timeoutId),
  );
}

export async function sendStreamingMessage(
  response: any,
): Promise<{ message: string; thinking: string; usedTokens: number }> {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  let message = '';
  let thinking = '';
  let usage = {
    totalTokens: 0,
  };
  let streamDone = false;

  try {
    while (!streamDone) {
      const { value, done } = await readWithTimeout(
        reader,
        STREAM_INACTIVITY_TIMEOUT_MS,
      );
      if (done) break;

      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') {
            streamDone = true;
            break;
          }

          try {
            const parsed: ClaudeStreamEvent = JSON.parse(data);
            if (parsed.text) {
              message += parsed.text;
            } else if (parsed.thinking) {
              thinking += parsed.thinking;
            } else if (parsed.type === 'usage') {
              usage.totalTokens = parsed.usage?.totalTokens || 0;
            }
          } catch (e) {
            console.error('Error parsing SSE data:', e);
          }
        }
      }
    }

    return { message, thinking, usedTokens: usage.totalTokens };
  } catch (error) {
    try {
      await reader.cancel();
    } catch {}
    console.error('Error parsing SSE data:', error);
    throw error;
  }
}
